import { Platform } from 'react-native';
import { useThemeStore } from '@/lib/state/theme-store';
import { lightColors, darkColors } from '@/lib/colors';

/**
 * Hook to get theme-aware colors
 * Returns the appropriate color palette based on current dark mode setting
 */
export function useThemeColors() {
  const isDark = useThemeStore((s) => s.isDark);
  return isDark ? darkColors : lightColors;
}

/**
 * Colors for pages rendered inside `PublicLayout`.
 *
 * On web, `PublicLayout` forces a dark surface (dark nav + footer + white
 * wordmark — the public site is dark-only by design), so the dark palette is
 * always used there regardless of the visitor's stored theme. Without this, a
 * default visitor (theme-store defaults to `isDark: false`) would get the light
 * palette and render near-black text on the dark surface — invisible.
 *
 * On native, `PublicLayout` is theme-aware, so the user's stored theme is kept.
 */
export function usePublicThemeColors() {
  const themed = useThemeColors();
  return Platform.OS === 'web' ? darkColors : themed;
}

/**
 * Hook to get colors for pre-authentication screens (landing, login, onboarding).
 * Always uses the user's stored preference (defaults to light mode).
 * Does NOT follow system/device dark mode automatically.
 */
export function useSystemThemeColors() {
  const isDark = useThemeStore((s) => s.isDark);
  return isDark ? darkColors : lightColors;
}

/**
 * Get colors for non-component contexts (use sparingly)
 * Prefer useThemeColors hook in components
 */
export function getThemeColors(isDark: boolean) {
  return isDark ? darkColors : lightColors;
}
